import { useEffect, useState } from "react";
import {
  EyeOff,
  Eye,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  createHideOverride,
  deleteHideOverride,
} from "../../../lib/data/availability";
import { findMatchingOverrides } from "../utils/hide-overrides";
import { useEventTitle } from "../../calendar-enrichment/useEventTitles";
import { buildGoogleCalendarEventUrl } from "../../../lib/google-calendar";

function genId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ovr-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

export default function HideEventDialog({
  open,
  onOpenChange,
  uid,
  event,
  hideOverrides,
  onMutated,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Two-step unhide: clicking "Unhide" opens an inline confirmation panel
  // explaining that re-engaging the event may auto-update votes on
  // overlapping poll slots. The user must click "Confirm unhide" to proceed.
  // Resets whenever the dialog closes or the event changes (defensive — the
  // parent re-uses one HideEventDialog instance for multiple events).
  const [confirmingUnhide, setConfirmingUnhide] = useState(false);
  useEffect(() => {
    setConfirmingUnhide(false);
    setError(null);
  }, [event?.eventId, event?.startUtc, open]);

  // Title is resolved client-side via the enrichment hook (see
  // docs/calendar-cache-privacy-plan.md — titles are not persisted at rest).
  // The hook MUST be called unconditionally; null req = no fetch.
  const titleEntry = useEventTitle(
    event && event.calendarId && event.eventId
      ? {
          provider: event.provider || "google",
          calendarId: event.calendarId,
          eventId: event.eventId,
        }
      : null,
  );

  if (!event) return null;

  const isRecurring = !!event.recurringEventId;
  const matches = findMatchingOverrides(event, hideOverrides);
  const isHidden = matches.length > 0;
  const resolvedTitle = titleEntry?.title || null;
  const titleErrored = !!titleEntry?.error;
  const headerTitle = resolvedTitle || (titleErrored ? "Calendar event" : "Loading…");
  // htmlLink is no longer persisted in the cache (privacy invariant). Build
  // it client-side from the structural identifiers.
  const htmlLink =
    event.calendarId && event.eventId
      ? buildGoogleCalendarEventUrl({
          calendarId: event.calendarId,
          eventId: event.eventId,
        })
      : null;

  async function hideSingle() {
    setBusy(true);
    setError(null);
    try {
      const id = genId();
      const payload = {
        calendarId: event.calendarId,
        scope: "single",
      };
      if (isRecurring) {
        payload.recurringEventId = event.recurringEventId;
        payload.instanceStart = event.originalStartTime || event.startUtc;
      } else {
        payload.eventId = event.eventId;
      }
      await createHideOverride(uid, id, payload);
      if (onMutated) onMutated();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to hide event.");
    } finally {
      setBusy(false);
    }
  }

  async function hideThisAndFuture() {
    setBusy(true);
    setError(null);
    try {
      const id = genId();
      await createHideOverride(uid, id, {
        calendarId: event.calendarId,
        scope: "thisAndFuture",
        recurringEventId: event.recurringEventId,
        instanceStart: event.originalStartTime || event.startUtc,
      });
      if (onMutated) onMutated();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to hide future events.");
    } finally {
      setBusy(false);
    }
  }

  async function unhideAll() {
    setBusy(true);
    setError(null);
    try {
      for (const ovr of matches) {
        if (ovr.id) {
          await deleteHideOverride(uid, ovr.id);
        }
      }
      if (onMutated) onMutated();
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to unhide event.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle data-testid="hide-dialog-title">{headerTitle}</DialogTitle>
              <DialogDescription>
                {titleErrored
                  ? "We couldn't load the title for this event from your calendar — you can still hide it."
                  : isHidden
                    ? "This event is currently hidden — it won't block any voting time."
                    : "Hide this event so it won't contribute to conflict detection."}
              </DialogDescription>
            </div>
            {htmlLink && (
              <a
                href={htmlLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in Google Calendar"
                title="Open in Google Calendar"
                className="flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                Google Calendar
              </a>
            )}
          </div>
        </DialogHeader>

        {error && (
          <p className="rounded-xl bg-rose-100 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="space-y-2 py-2 text-sm">
          {isHidden && !confirmingUnhide && (
            <button
              type="button"
              onClick={() => setConfirmingUnhide(true)}
              disabled={busy}
              data-testid="hide-dialog-unhide-button"
              className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              <Eye className="h-4 w-4" />
              <span>
                <span className="block font-medium">Unhide event</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  Removes {matches.length} hide override
                  {matches.length === 1 ? "" : "s"}.
                </span>
              </span>
            </button>
          )}

          {isHidden && confirmingUnhide && (
            <div
              data-testid="hide-dialog-unhide-confirmation"
              className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle
                  className="mt-0.5 h-4 w-4 shrink-0"
                  aria-hidden="true"
                />
                <div className="space-y-2">
                  <p className="font-medium">Unhide this event?</p>
                  <p className="text-xs leading-relaxed text-amber-900/80 dark:text-amber-100/80">
                    Quest Scheduler will re-add this event to conflict
                    detection on all your polls and re-share it with anyone
                    you&apos;ve granted availability to. If the event overlaps
                    a slot you&apos;ve already voted on, your auto-votes may
                    be updated to reflect the conflict.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmingUnhide(false)}
                  disabled={busy}
                  data-testid="hide-dialog-unhide-cancel"
                  className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={unhideAll}
                  disabled={busy}
                  data-testid="hide-dialog-unhide-confirm"
                  className="rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-50"
                >
                  {busy ? "Unhiding..." : "Confirm unhide"}
                </button>
              </div>
            </div>
          )}

          {!isHidden && !isRecurring && (
            <button
              type="button"
              onClick={hideSingle}
              disabled={busy}
              className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
            >
              <EyeOff className="h-4 w-4" />
              Hide this event
            </button>
          )}

          {!isHidden && isRecurring && (
            <>
              <button
                type="button"
                onClick={hideSingle}
                disabled={busy}
                className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <EyeOff className="h-4 w-4" />
                Hide this event
              </button>
              <button
                type="button"
                onClick={hideThisAndFuture}
                disabled={busy}
                className="flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <RefreshCw className="h-4 w-4" />
                Hide this and all following
              </button>
            </>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
